import { getActiveEnvironmentApiBaseUrl } from "./environmentConfig";

async function request(path, options = {}) {
  const response = await fetch(`${getActiveEnvironmentApiBaseUrl()}${path}`, {
    cache: "no-store",
    ...options
  });

  if (response.status === 204) {
    return null;
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const detail = payload?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((item) => item?.msg || "Invalid input").join(", ")
          : `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return payload;
}

function authHeaders(token) {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json"
  };
}

export async function loginWebBuilder(username, password) {
  return request("/web-builder/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  });
}

export async function registerWebBuilderAccount(friendlyId, password) {
  return request("/web-builder/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ friendly_id: friendlyId, password })
  });
}

export async function checkWebBuilderAccountEligibility(friendlyId) {
  return request("/web-builder/auth/register/check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ friendly_id: friendlyId })
  });
}

export async function getCurrentWebBuilderUser(token) {
  return request("/web-builder/auth/me", {
    method: "GET",
    headers: authHeaders(token)
  });
}

export async function changeOwnWebBuilderPassword(token, currentPassword, newPassword) {
  return request("/web-builder/auth/change-password", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
  });
}

export async function listWebBuilderUsers(token) {
  return request("/web-builder/admin/users", {
    method: "GET",
    headers: authHeaders(token)
  });
}

export async function createWebBuilderUser(token, payload) {
  return request("/web-builder/admin/users", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload)
  });
}

export async function updateWebBuilderUser(token, userId, payload) {
  return request(`/web-builder/admin/users/${userId}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(payload)
  });
}

export async function changeWebBuilderUserPassword(token, userId, password) {
  return request(`/web-builder/admin/users/${userId}/password`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ password })
  });
}

export async function deleteWebBuilderUser(token, userId) {
  return request(`/web-builder/admin/users/${userId}`, {
    method: "DELETE",
    headers: authHeaders(token)
  });
}

export async function listWebBuilderLevels(token) {
  return request("/web-builder/levels", {
    method: "GET",
    headers: authHeaders(token)
  });
}

export async function getWebBuilderLevel(token, levelId) {
  return request(`/web-builder/levels/${levelId}`, {
    method: "GET",
    headers: authHeaders(token)
  });
}

export async function createWebBuilderLevel(token, payload) {
  return request("/web-builder/levels", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload)
  });
}

export async function updateWebBuilderLevel(token, levelId, payload) {
  return request(`/web-builder/levels/${levelId}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(payload)
  });
}

export async function deleteWebBuilderLevel(token, levelId) {
  return request(`/web-builder/levels/${levelId}`, {
    method: "DELETE",
    headers: authHeaders(token)
  });
}

export async function listAdminWebBuilderLevels(
  token,
  { limit = 100, offset = 0, search, creator_user_id, is_blocked } = {}
) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (search) {
    params.set("search", search);
  }
  if (creator_user_id) {
    params.set("creator_user_id", creator_user_id);
  }
  if (typeof is_blocked === "boolean") {
    params.set("is_blocked", String(is_blocked));
  }

  return request(`/web-builder/admin/levels?${params.toString()}`, {
    method: "GET",
    headers: authHeaders(token)
  });
}

export async function getAdminWebBuilderLevel(token, levelId) {
  return request(`/web-builder/admin/levels/${levelId}`, {
    method: "GET",
    headers: authHeaders(token)
  });
}

export async function moderateAdminWebBuilderLevel(token, levelId, payload) {
  return request(`/web-builder/admin/levels/${levelId}/moderation`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(payload)
  });
}

export async function deleteAdminWebBuilderLevel(token, levelId) {
  return request(`/web-builder/admin/levels/${levelId}`, {
    method: "DELETE",
    headers: authHeaders(token)
  });
}
